import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePathname } from "@/i18n/navigation";
import { getSupportedDeviceList } from "@/services/misc/supportedDeviceList";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Spinner } from "../fallbacks/Spinner";
import FooterLink from "../links/FooterLink";

function CompatibleDevices({
  setOpen,
  setShowSuccessModal,
  setSelectedDevice,
}: {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSuccessModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedDevice: React.Dispatch<React.SetStateAction<string>>;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const t = useTranslations(`Shared.compatibleModal`);

  const pathname = usePathname();

  const { data: supportedDevices = [], isLoading } = useQuery({
    queryKey: ["supported-devices"],
    queryFn: async () => {
      const res = await getSupportedDeviceList();
      if (!res.status) {
        throw new Error(res.message);
      }
      return res.data;
    },
  });
  const filteredBrands = Array.from(
    new Set(supportedDevices.map((device) => device.type)),
  );
  const filteredDevices = supportedDevices.filter((device) => {
    return (
      device.type
        .toLowerCase()
        .trim()
        .includes(selectedBrand.toLowerCase().trim()) &&
      device.model
        .toLowerCase()
        .trim()
        .includes(searchQuery.toLowerCase().trim())
    );
  });
  return (
    <>
      <p className="text-body-lg font-500">{t(`modalTitle`)}</p>
      {pathname !== "/esim-compatible/" && (
        <p className="flex flex-col gap-1 text-sm opacity-80 sm:flex-row">
          {t.rich(`description`, {
            FooterLink: (text) => (
              <FooterLink
                href="/esim-compatible/"
                className="text-primary underline underline-offset-4"
              >
                {text}
              </FooterLink>
            ),
          })}
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          placeholder={t(`searchPlaceholder`)}
          className="rounded-[0.6rem] border shadow-none"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Select
          value={selectedBrand}
          onValueChange={(value) => setSelectedBrand(value)}
        >
          <SelectTrigger
            className="h-full rounded-[0.6rem] border shadow-none"
            aria-label={t(`selectLabel`)}
          >
            <SelectValue placeholder={t(`selectLabel`)} />
          </SelectTrigger>
          <SelectContent>
            {filteredBrands.map((brand, index) => (
              <SelectItem
                key={index}
                value={brand}
                className="cursor-pointer hover:bg-muted"
              >
                {brand}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="barMini max-h-[280px] min-h-[280px] overflow-auto rounded-[0.6rem] border px-2 py-4">
        <div className="flex flex-col gap-1">
          {isLoading ? (
            <Spinner />
          ) : filteredDevices.length > 0 ? (
            filteredDevices.map((device, index) => (
              <p
                className="cursor-pointer rounded-[0.6rem] px-3 py-2 text-body-base transition-all hover:bg-muted"
                key={index}
                onClick={() => {
                  setSelectedDevice(device.model);
                  setShowSuccessModal(true);
                  setOpen(false);
                }}
              >
                {device.model}
              </p>
            ))
          ) : (
            <p className="cursor-pointer rounded-[0.6rem] px-3 py-2 text-body-base transition-all hover:bg-muted">
              {t(`noMatch`)}
            </p>
          )}
        </div>
      </div>
    </>
  );
}

export default CompatibleDevices;
